import React, { useState } from 'react';
import { Calendar, Filter, Unlock, User } from 'lucide-react';
import { PageContainer } from '../../../shared/components/PageContainer';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { useToast } from '../../../shared/components/Toast';
import { usePermissions } from '../../../shared/hooks/usePermissions';
import { FEATURES } from '../../../shared/lib/permissions';
import { openAuthenticatedFile } from '../../../shared/lib/files';
import type { NFStatus, NotaFiscal } from '../../../shared/types';
import { NFTable } from '../components/NFTable';
import { LiberarNFModal } from '../components/LiberarNFModal';
import { RejeitarNFModal } from '../components/RejeitarNFModal';
import {
  useAprovarNotaFiscal,
  useCancelarLiberacao,
  useColaboradoresLiberaveis,
  useFaturarNotaFiscal,
  useNotasFiscais,
} from '../hooks';
import { NF_STATUS_META, NF_STATUS_ORDER, apiErrorMessage, formatCompetencia } from '../constants';

type ConfirmAction = { kind: 'aprovar' | 'faturar' | 'cancelar'; nota: NotaFiscal };

const CONFIRM_TEXT: Record<ConfirmAction['kind'], { title: string; message: (n: NotaFiscal) => string; label: string }> = {
  aprovar: {
    title: 'Aprovar nota fiscal',
    message: (n) => `Aprovar a NF ${n.number ?? ''} de ${n.collaboratorName ?? 'colaborador'} (${formatCompetencia(n.referenceMonth)})?`,
    label: 'Aprovar',
  },
  faturar: {
    title: 'Marcar como faturada',
    message: (n) => `Marcar a NF ${n.number ?? ''} como faturada? Depois disso a nota não pode mais ser alterada.`,
    label: 'Faturar',
  },
  cancelar: {
    title: 'Cancelar liberação',
    message: (n) =>
      `Cancelar a liberação de ${formatCompetencia(n.referenceMonth)} para ${n.collaboratorName ?? 'o colaborador'}? A competência poderá ser liberada de novo.`,
    label: 'Cancelar liberação',
  },
};

/** Operação do financeiro: liberar, analisar e faturar as NFs de todos. */
const GestaoNotasFiscaisPage: React.FC = () => {
  const toast = useToast();
  const { can } = usePermissions();
  const [status, setStatus] = useState<NFStatus | ''>('');
  const [referenceMonth, setReferenceMonth] = useState('');
  const [collaboratorId, setCollaboratorId] = useState('');
  const [liberando, setLiberando] = useState(false);
  const [rejeitando, setRejeitando] = useState<NotaFiscal | null>(null);
  const [confirm, setConfirm] = useState<ConfirmAction | null>(null);

  const { data, isLoading, isError } = useNotasFiscais({
    status: status || undefined,
    referenceMonth: referenceMonth || undefined,
    collaboratorId: collaboratorId || undefined,
    limit: 100,
  });
  const { data: colaboradores = [] } = useColaboradoresLiberaveis('');
  const aprovar = useAprovarNotaFiscal();
  const faturar = useFaturarNotaFiscal();
  const cancelar = useCancelarLiberacao();
  const busy = aprovar.isPending || faturar.isPending || cancelar.isPending;

  const podeEditar = can(FEATURES.notasFiscaisLiberacao, 'edit');
  const podeLiberar = can(FEATURES.notasFiscaisLiberacao, 'create');
  const podeCancelar = can(FEATURES.notasFiscaisLiberacao, 'delete');

  async function executarConfirmacao() {
    if (!confirm) return;
    const { kind, nota } = confirm;
    try {
      if (kind === 'aprovar') await aprovar.mutateAsync(nota.id);
      if (kind === 'faturar') await faturar.mutateAsync(nota.id);
      if (kind === 'cancelar') await cancelar.mutateAsync(nota.id);
      toast.success(
        kind === 'aprovar' ? 'Nota fiscal aprovada.' : kind === 'faturar' ? 'Nota fiscal faturada.' : 'Liberação cancelada.',
      );
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Não foi possível concluir a ação.'));
    } finally {
      setConfirm(null);
    }
  }

  const handleDownloadPDF = async (nota: NotaFiscal) => {
    if (nota.pdfUrl) await openAuthenticatedFile(nota.pdfUrl);
  };

  return (
    <PageContainer
      title="Gestão de notas fiscais"
      subtitle="Libere a competência para o colaborador enviar a NF, analise as notas enviadas e marque como faturadas."
      breadcrumbs={[{ label: 'Notas fiscais', to: '/notas-fiscais' }, { label: 'Gestão' }]}
      action={
        podeLiberar && (
          <button
            type="button"
            onClick={() => setLiberando(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            <Unlock className="h-4 w-4" /> Liberar NF
          </button>
        )
      }
    >
      <div className="grid gap-3 rounded-lg border border-gray-200 bg-white p-4 sm:grid-cols-3">
        <AutocompleteSelect
          label="Status"
          icon={<Filter className="h-4 w-4" />}
          options={[
            { value: '', label: 'Todos os status' },
            ...NF_STATUS_ORDER.map((s) => ({ value: s, label: NF_STATUS_META[s].label })),
          ]}
          value={status}
          onChange={(v) => setStatus(v as NFStatus | '')}
        />
        <AutocompleteSelect
          label="Colaborador"
          icon={<User className="h-4 w-4" />}
          options={[{ value: '', label: 'Todos os colaboradores' }, ...colaboradores.map((c) => ({ value: c.id, label: c.name }))]}
          value={collaboratorId}
          onChange={setCollaboratorId}
        />
        <div className="relative">
          <Calendar className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="month"
            aria-label="Competência"
            value={referenceMonth}
            onChange={(e) => setReferenceMonth(e.target.value)}
            className="h-14 w-full rounded-lg border border-gray-300 pl-10 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      {isError ? (
        <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">Não foi possível carregar as notas fiscais.</div>
      ) : isLoading ? (
        <div className="text-sm text-gray-500">Carregando notas fiscais...</div>
      ) : (
        <NFTable
          notas={data?.data ?? []}
          showCollaborator
          emptyMessage="Nenhuma nota fiscal com os filtros selecionados."
          onDownloadPDF={handleDownloadPDF}
          onAprovar={podeEditar ? (nota) => setConfirm({ kind: 'aprovar', nota }) : undefined}
          onRejeitar={podeEditar ? setRejeitando : undefined}
          onFaturar={podeEditar ? (nota) => setConfirm({ kind: 'faturar', nota }) : undefined}
          onCancelar={podeCancelar ? (nota) => setConfirm({ kind: 'cancelar', nota }) : undefined}
        />
      )}

      {liberando && <LiberarNFModal onClose={() => setLiberando(false)} onReleased={(m) => toast.success(m)} />}
      {rejeitando && (
        <RejeitarNFModal nota={rejeitando} onClose={() => setRejeitando(null)} onRejected={(m) => toast.success(m)} />
      )}
      <ConfirmDialog
        open={!!confirm}
        title={confirm ? CONFIRM_TEXT[confirm.kind].title : ''}
        message={confirm ? CONFIRM_TEXT[confirm.kind].message(confirm.nota) : ''}
        confirmLabel={confirm ? CONFIRM_TEXT[confirm.kind].label : undefined}
        danger={confirm?.kind === 'cancelar'}
        busy={busy}
        onConfirm={executarConfirmacao}
        onCancel={() => setConfirm(null)}
      />
    </PageContainer>
  );
};

export default GestaoNotasFiscaisPage;
